import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, CircularProgress, Alert, Button, useTheme } from '@mui/material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const HydraChat: React.FC = () => {
  const { token, isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const theme = useTheme();
  
  useEffect(() => {
    // Get a secure token for Hydra Chat
    const fetchToken = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!isAuthenticated || !token) {
          throw new Error('You must be logged in to access Hydra Chat');
        }
        
        const response = await axios.get('/api/hydra-chat-token', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data && response.data.token) {
          setAccessToken(response.data.token);
          setLoading(false);
        } else {
          throw new Error('Invalid token response');
        }
      } catch (err: any) {
        console.error('Error fetching Hydra Chat token:', err);
        if (err.response && err.response.status === 403) {
          setError('Your session has expired. Please log out and log back in.');
        } else {
          setError('Failed to authenticate with Hydra Chat. Please try again later.');
        }
        setLoading(false);
      }
    };
    
    fetchToken();
  }, [token, isAuthenticated]);
  
  // Create URL with token authentication
  const getChatUrl = () => {
    if (!accessToken) return '';
    return `https://chat.hydralyfe.com.au/?token=${accessToken}&portal=bettermd&auto_login=true&source=bettermd_portal`;
  };
  
  return (
    <Paper 
      elevation={3} 
      sx={{ 
        p: 3, 
        mb: 3,
        minHeight: '700px',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <CircularProgress size={60} thickness={4} sx={{ mb: 3 }} />
          <Typography variant="h6" color="text.secondary">
            Connecting to Hydra Chat...
          </Typography>
        </Box>
      ) : error ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Alert severity="error" sx={{ mb: 2, maxWidth: '600px' }}>{error}</Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Please try refreshing the page or contact support if the problem persists.
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" component="h1" sx={{ color: theme.palette.primary.main }}>
              Hydra Chat AI Assistant
            </Typography>
            <Button 
              variant="outlined"
              size="small"
              onClick={() => window.open(getChatUrl(), '_blank')}
            >
              Open in New Tab
            </Button>
          </Box>
          
          <Box sx={{ 
            flexGrow: 1,
            width: '100%', 
            height: 'calc(100vh - 180px)', 
            border: '1px solid #eee', 
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}>
            <iframe 
              src={getChatUrl()}
              title="Hydra Chat AI" 
              width="100%" 
              height="100%" 
              style={{ border: 'none' }}
              allow="microphone; camera"
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-downloads allow-storage-access-by-user-activation allow-top-navigation-by-user-activation"
            />
          </Box>
        </>
      )}
    </Paper>
  );
};

export default HydraChat;